import { doc, getDoc, onSnapshot } from 'firebase/firestore';

import { collections, db } from 'firebaseConfig';
import { canRole, normalizeRole, WORKSPACE_ROLES } from 'security/rbac';

function workspaceDocRef(workspaceKey) {
  return doc(db, collections.workspaces, workspaceKey);
}

function workspaceMemberDocRef(workspaceKey, userKey) {
  return doc(db, collections.workspaces, workspaceKey, 'members', userKey);
}

function inferRoleFromRows(workspaceRow, memberRow, userKey) {
  if (!workspaceRow || !userKey) return null;
  if (workspaceRow.ownerKey === userKey) return WORKSPACE_ROLES.OWNER;
  return normalizeRole(memberRow?.role) || null;
}

export function canWorkspaceRole(role, action) {
  return canRole(role, action);
}

export async function getWorkspaceRoleForUser({ workspaceKey, userKey, workspace }) {
  if (!workspaceKey || !userKey) return null;

  const workspaceRow = workspace || (await getDoc(workspaceDocRef(workspaceKey))).data() || null;
  if (!workspaceRow) return null;
  if (workspaceRow.ownerKey === userKey) return WORKSPACE_ROLES.OWNER;

  const memberSnap = await getDoc(workspaceMemberDocRef(workspaceKey, userKey));
  if (!memberSnap.exists()) return null;

  return normalizeRole(memberSnap.data()?.role);
}

export function subscribeToWorkspaceRole({ workspaceKey, userKey, workspace, callback, onError }) {
  if (!workspaceKey || !userKey || typeof callback !== 'function') {
    return () => {};
  }

  const workspaceRow = workspace || null;
  if (workspaceRow?.ownerKey === userKey) {
    callback(WORKSPACE_ROLES.OWNER);
    return () => {};
  }

  return onSnapshot(
    workspaceMemberDocRef(workspaceKey, userKey),
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }

      const role = normalizeRole(snap.data()?.role);
      callback(role || null);
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      } else {
        console.error('[WorkspaceRoleService] subscribeToWorkspaceRole error:', error);
      }
      callback(null);
    }
  );
}

export function buildWorkspaceMemberMap(rows = []) {
  return rows.reduce((acc, row) => {
    if (!row?.userKey) return acc;
    acc[row.userKey] = row;
    return acc;
  }, {});
}

export function getRoleFromWorkspaceMemberMap({ workspace, userKey, memberByUserKey }) {
  return inferRoleFromRows(workspace, memberByUserKey?.[userKey] || null, userKey);
}
